import React, { useState } from 'react';
import { JourneySetup, JourneyStage, JourneyMarket } from '../../types/journey';
import StageSelection from './StageSelection';
import MarketSelection from './MarketSelection';
import DetailedEvaluation from './DetailedEvaluation';
import { useLanguage } from '../../context/LanguageContext';
import './StartupJourneySetup.css';

interface StartupJourneySetupProps {
  onComplete: (setup: JourneySetup) => void;
  onCancel?: () => void;
}

type SetupStep = 'stage' | 'market' | 'evaluation' | 'review';

const StartupJourneySetup: React.FC<StartupJourneySetupProps> = ({
  onComplete,
  onCancel
}) => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState<SetupStep>('stage');
  const [setup, setSetup] = useState<Partial<JourneySetup>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDetailedEvaluation, setShowDetailedEvaluation] = useState(false);

  const validateStep = (step: SetupStep): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 'stage':
        if (!setup.stage) {
          newErrors.stage = t('journey.setupErrorStage');
        }
        break;
      case 'market':
        if (!setup.market) {
          newErrors.market = t('journey.setupErrorMarket');
        }
        break;
      case 'evaluation':
        if (showDetailedEvaluation && setup.evaluation) {
          const evaluation = setup.evaluation;
          const requiredFields = ['idea', 'skills', 'team', 'funding', 'market', 'tech', 'network', 'time'];
          
          for (const field of requiredFields) {
            if (evaluation[field as keyof typeof evaluation] === undefined) {
              newErrors[field] = t('journey.setupErrorRateField', { field });
            }
          }
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;

    switch (currentStep) {
      case 'stage':
        setCurrentStep('market');
        break;
      case 'market':
        setCurrentStep('evaluation');
        break;
      case 'evaluation':
        setCurrentStep('review');
        break;
      case 'review':
        onComplete(setup as JourneySetup);
        break;
    }
  };

  const handleBack = () => {
    switch (currentStep) {
      case 'market':
        setCurrentStep('stage');
        break;
      case 'evaluation':
        setCurrentStep('market');
        break;
      case 'review':
        setCurrentStep('evaluation');
        break;
    }
  };

  const handleSkipEvaluation = () => {
    setShowDetailedEvaluation(false);
    setSetup(prev => ({ ...prev, evaluation: undefined }));
    setCurrentStep('review');
  };

  const updateSetup = (updates: Partial<JourneySetup>) => {
    setSetup(prev => ({ ...prev, ...updates }));
    setErrors({});
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'stage':
        return (
          <StageSelection
            value={setup.stage}
            onChange={(stage) => updateSetup({ stage: stage as JourneyStage })}
            error={errors.stage}
          />
        );
      case 'market':
        return (
          <MarketSelection
            value={setup.market}
            onChange={(market) => updateSetup({ market: market as JourneyMarket })}
            error={errors.market}
          />
        );
      case 'evaluation':
        return (
          <DetailedEvaluation
            value={setup.evaluation}
            onChange={(evaluation) => updateSetup({ evaluation })}
            errors={errors}
            showForm={showDetailedEvaluation}
            onToggleForm={setShowDetailedEvaluation}
          />
        );
      case 'review':
        return (
          <div className="setup-review">
            <h3>{t('journey.setupReviewTitle')}</h3>
            <div className="review-item">
              <strong>{t('journey.stage')}:</strong> {setup.stage}
            </div>
            <div className="review-item">
              <strong>{t('journey.market')}:</strong> {setup.market}
            </div>
            {setup.evaluation && (
              <div className="review-item">
                <strong>{t('journey.setupDetailedEvaluation')}:</strong> {t('journey.setupCompleted')}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'stage':
        return t('journey.setupStepStageTitle');
      case 'market':
        return t('journey.setupStepMarketTitle');
      case 'evaluation':
        return t('journey.setupStepEvaluationTitle');
      case 'review':
        return t('journey.setupStepReviewTitle');
      default:
        return '';
    }
  };

  const isLastStep = currentStep === 'review';
  const isFirstStep = currentStep === 'stage';

  return (
    <div className="startup-journey-setup">
      <div className="setup-container">
        <div className="setup-header">
          <h2>{t('journey.setupTitle')}</h2>
          <div className="step-indicator">
            <div className={`step ${currentStep === 'stage' ? 'active' : 'completed'}`}>1</div>
            <div className={`step ${currentStep === 'market' ? 'active' : currentStep === 'evaluation' || currentStep === 'review' ? 'completed' : ''}`}>2</div>
            <div className={`step ${currentStep === 'evaluation' ? 'active' : currentStep === 'review' ? 'completed' : ''}`}>3</div>
            <div className={`step ${currentStep === 'review' ? 'active' : ''}`}>4</div>
          </div>
        </div>

        <div className="setup-content">
          <h3 className="step-title">{getStepTitle()}</h3>
          {renderStepContent()}
        </div>

        <div className="setup-actions">
          <div className="action-buttons-left">
            {!isFirstStep && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleBack}
              >
                {t('journey.setupBack')}
              </button>
            )}
            
            {onCancel && (
              <button
                type="button"
                className="btn btn-text"
                onClick={onCancel}
              >
                {t('journey.setupCancel')}
              </button>
            )}
          </div>
          
          <div className="action-buttons-right">
            {currentStep === 'evaluation' && !showDetailedEvaluation && (
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleSkipEvaluation}
              >
                {t('journey.setupSkipForNow')}
              </button>
            )}
            
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleNext}
            >
              {isLastStep ? t('journey.setupStartJourney') : t('journey.setupNext')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartupJourneySetup;